import { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Grid, Typography, Box,
  FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getIcon } from '../../../utils/IconHandler';
import { getLocations } from '../../../services/LocationService';
import B2BClientService from '../../../services/B2BClientService';

const EditB2BClientModal = ({ open, onClose, client, onSave }) => {
  const { t } = useTranslation();
  
  const [editedClient, setEditedClient] = useState({
    razonSocial: '',
    nombreFantasia: '',
    rut: '',
    direccionFiscal: '',
    contacto: '',
    telefono: '',
    email: '',
    creditDays: '',
    creditLimit: '',
    generalDiscount: '',
    commercialProfileId: '',
    clientType: 'EXTERNAL',
    defaultDestinationLocationId: '',
    notes: '',
    priceExceptions: []
  });
  const [locations, setLocations] = useState([]);
  const [commercialProfiles, setCommercialProfiles] = useState([]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const loadInitialData = async () => {
      try {
        const [locationsData, profilesData] = await Promise.all([
          getLocations(),
          B2BClientService.getCommercialProfiles()
        ]);
        setLocations((locationsData || []).filter(location => !location.isPlant));
        setCommercialProfiles(profilesData || []);
      } catch (error) {
        setLocations([]);
        setCommercialProfiles([]);
      }
    };

    loadInitialData();
  }, [open]);

  useEffect(() => {
    if (client) {
      setEditedClient({
        nombreFantasia: client.nombreFantasia || '',
        razonSocial: client.razonSocial || '',
        rut: client.rut || '',
        direccionFiscal: client.direccionFiscal || '',
        contacto: client.contacto || '',
        telefono: client.telefono || '',
        email: client.email || '',
        creditDays: client.creditDays || '',
        creditLimit: client.creditLimit || '',
        generalDiscount: client.generalDiscount || '',
        commercialProfileId: client.commercialProfileId || '',
        clientType: client.clientType || 'EXTERNAL',
        defaultDestinationLocationId: client.defaultDestinationLocationId || '',
        notes: client.notes || '',
        priceExceptions: client.priceExceptions || []
      });
    }
  }, [client]);

  const handleFieldChange = (field, value) => {
    setEditedClient(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = () => {
    // Validaciones básicas
    if (!editedClient.razonSocial.trim()) {
      alert(t('business_name_required'));
      return;
    }
    if (!editedClient.nombreFantasia.trim()) {
      alert(t('fantasys_name_required'));
      return;
    }
    if (!editedClient.rut.trim()) {
      alert(t('rut_required'));
      return;
    }
    if (!editedClient.contacto.trim()) {
      alert(t('contact_name_required'));
      return;
    }
    if (editedClient.clientType === 'FRANCHISE' && !editedClient.defaultDestinationLocationId) {
      alert('Seleccioná la franquicia asignada');
      return;
    }

    // Preparar los datos según la estructura del backend
    const clientData = {
      id: client.id, // Incluir el ID del cliente original
      rut: editedClient.rut,
      razonSocial: editedClient.razonSocial,
      nombreFantasia: editedClient.nombreFantasia,
      direccionFiscal: editedClient.direccionFiscal,
      telefono: editedClient.telefono,
      email: editedClient.email,
      contacto: editedClient.contacto,
      creditDays: editedClient.creditDays || 30, // Valor por defecto si está vacío
      creditLimit: editedClient.creditLimit || 0.0, // Valor por defecto si está vacío
      generalDiscount: 0.0,
      commercialProfileId: editedClient.commercialProfileId !== '' ? Number(editedClient.commercialProfileId) : null,
      clientType: editedClient.clientType,
      defaultDestinationLocationId:
        editedClient.clientType === 'FRANCHISE' && editedClient.defaultDestinationLocationId !== ''
          ? Number(editedClient.defaultDestinationLocationId)
          : null,
      notes: editedClient.notes,
      priceExceptions: []
    };

    onSave(clientData);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {getIcon('settings')}
          {t('edit_b2b_client')}
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Grid container spacing={3} sx={{ mt: 1 }}>
          {/* Datos Básicos */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ borderBottom: 1, borderColor: 'divider', pb: 1 }}>
              {t('basic_data')}
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <TextField
              label={t('business_name')}
              fullWidth
              value={editedClient.razonSocial}
              onChange={(e) => handleFieldChange('razonSocial', e.target.value)}
              required
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label={t('fantasy_name')}
              fullWidth
              value={editedClient.nombreFantasia}
              onChange={(e) => handleFieldChange('nombreFantasia', e.target.value)}
              required
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label={t('rut')}
              fullWidth
              value={editedClient.rut}
              onChange={(e) => handleFieldChange('rut', e.target.value)}
              required
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              label={t('address')}
              fullWidth
              value={editedClient.direccionFiscal}
              onChange={(e) => handleFieldChange('direccionFiscal', e.target.value)}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              label={t('main_contact')}
              fullWidth
              value={editedClient.contacto}
              onChange={(e) => handleFieldChange('contacto', e.target.value)}
              required
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              label={t('phone')}
              fullWidth
              value={editedClient.telefono}
              onChange={(e) => handleFieldChange('telefono', e.target.value)}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              label={t('email')}
              fullWidth
              type="email"
              value={editedClient.email}
              onChange={(e) => handleFieldChange('email', e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Tipo de cliente</InputLabel>
              <Select
                value={editedClient.clientType}
                label="Tipo de cliente"
                onChange={e => handleFieldChange('clientType', e.target.value)}
              >
                <MenuItem value="EXTERNAL">Cliente externo</MenuItem>
                <MenuItem value="FRANCHISE">Franquicia</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {editedClient.clientType === 'FRANCHISE' && (
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Franquicia asignada</InputLabel>
                <Select
                  value={editedClient.defaultDestinationLocationId}
                  label="Franquicia asignada"
                  onChange={e => handleFieldChange('defaultDestinationLocationId', e.target.value)}
                >
                  {locations.map(location => (
                    <MenuItem key={location.id} value={location.id}>
                      {location.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}


          {/* Acuerdo Comercial */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ borderBottom: 1, borderColor: 'divider', pb: 1, mt: 2 }}>
              {t('commercial_agreement')}
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <TextField
              label={t('credit_days')}
              fullWidth
              type="number"
              value={editedClient.creditDays || ''}
              onChange={(e) => handleFieldChange('creditDays', parseInt(e.target.value) || '')}
              placeholder="30"
              InputProps={{ endAdornment: t('days') }}
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <TextField
              label={t('credit_limit')}
              fullWidth
              type="number"
              value={editedClient.creditLimit || ''}
              onChange={(e) => handleFieldChange('creditLimit', parseFloat(e.target.value) || '')}
              placeholder="10000"
              InputProps={{ startAdornment: '$' }}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Perfil comercial</InputLabel>
              <Select
                value={editedClient.commercialProfileId}
                label="Perfil comercial"
                onChange={(e) => handleFieldChange('commercialProfileId', e.target.value)}
              >
                <MenuItem value="">Sin perfil</MenuItem>
                {commercialProfiles.map(profile => (
                  <MenuItem key={profile.id} value={profile.id}>
                    {profile.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              label={t('notes')}
              fullWidth
              multiline
              rows={3}
              value={editedClient.notes}
              onChange={(e) => handleFieldChange('notes', e.target.value)}
              placeholder={t('notes_placeholder')}
            />
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>
          {t('cancel')}
        </Button>
        <Button 
          variant="contained" 
          onClick={handleSave}
        >
          {t('save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditB2BClientModal;
